import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { connect } from "react-redux";

import toJs from "../../../hoc/toJS";
import select from "../../../utils/select";

import { getAllPurpose } from "../../../reducer/scientificResearch/action";

import JournalInternational from "../../ScientificResearch/ScientificResearchInsertForm/JournalInternational";
import JournalDomestic from "../../ScientificResearch/ScientificResearchInsertForm/JournalDomestic";
import Conference from "../../ScientificResearch/ScientificResearchInsertForm/Conference";
import ConferenceDomestic from "../../ScientificResearch/ScientificResearchInsertForm/ConferenceDomestic";
import MultiFileManager from "../../File/MultiFileManager/MultiFileManager";

import { apiCall } from "../../../utils/api";
import { API_URLS } from "../../../constants/api";

const NOOP = () => {};

const LinhVucBaiBao = [
  { value: "GDDT", label: "Giáo dục đào tạo" },
  { value: "KT", label: "Kinh tế" },
  { value: "QTKD", label: "Quản trị kinh doanh" },
  { value: "QL", label: "Quản lý" },
  { value: "KHAC", label: "Khác" },
];

const getTypeTitle = (type) => {
  switch (type) {
    case "scientificResearchJournal":
    case "journalInternational":
      return "trên tạp chí quốc tế";
    case "scientificResearchJournalDomestic":
    case "journalDomestic":
      return "trên tạp chí trong nước";
    case "scientificResearchConference":
    case "conference":
      return "trên hội thảo quốc tế";
    case "scientificResearchConferenceDomestic":
    case "conferenceDomestic":
      return "trên hội thảo trong nước";
    default:
      return "";
  }
};

function ScientificResearchViewModal({ open, onClose, data, isFetching, profileList, purposes, getAllPurpose }) {
  // The sub-forms (JournalInternational/Domestic/Conference/...) render the Mục đích
  // dropdown using props.purposes from Redux. When this modal is opened from
  // /reward_management without visiting /work first, that slice can be empty → the
  // select shows no options and the value disappears. Ensure it's loaded.
  useEffect(() => {
    if (open && (!purposes || purposes.length === 0)) {
      getAllPurpose?.();
    }
  }, [open, purposes, getAllPurpose]);

  // The API returns nested structure: { scientificResearch: {...}, journal: {...}, ... }
  // We need to flatten it like the update modal expects
  const scientificResearch = data?.scientificResearch || data || {};
  const journal = data?.journal;
  const journalDomestic = data?.journalDomestic;
  const conference = data?.conference || data?.conferenceDomestic;

  // Map new type names to old filter names for sub-form components
  const typeMapping = {
    scientificResearchJournal: "journalInternational",
    scientificResearchJournalDomestic: "journalDomestic",
    scientificResearchConference: "conference",
    scientificResearchConferenceDomestic: "conferenceDomestic",
  };
  const originalType = scientificResearch?.type || "";
  const filter = typeMapping[originalType] || originalType;

  const values = useMemo(() => {
    if (!scientificResearch || !scientificResearch.id) return null;

    // Convert listAuthor array to string
    let listAuthorStr = "";
    if (Array.isArray(scientificResearch.listAuthor)) {
      listAuthorStr = scientificResearch.listAuthor.join(", ");
    } else {
      listAuthorStr = scientificResearch.listAuthor || "";
    }

    // Flatten the nested structure to match what sub-forms expect
    return {
      ...scientificResearch,
      listAuthor: listAuthorStr,
      acceptedDay: scientificResearch.acceptedDay ? new Date(scientificResearch.acceptedDay) : null,
      submissionDay: scientificResearch.submissionDay ? new Date(scientificResearch.submissionDay) : null,
      // Set the appropriate IDs based on type
      journalId: journal?.id || scientificResearch.journalId,
      journalDomesticId: journalDomestic?.id || scientificResearch.journalDomesticId,
      conferenceId:
        originalType === "scientificResearchConference" || originalType === "conference"
          ? conference?.id || scientificResearch.conferenceId
          : originalType === "scientificResearchConferenceDomestic" || originalType === "conferenceDomestic"
            ? conference?.id || scientificResearch.conferenceDomesticId
            : null,
      conferenceDomesticId: null,
    };
  }, [scientificResearch, journal, journalDomestic, conference, originalType]);

  // Extract fileUrls and members from nested structure
  const fileUrls = useMemo(() => {
    const fileUrl = scientificResearch?.fileUrl || "";
    return fileUrl ? fileUrl.split("||").filter(Boolean) : [];
  }, [scientificResearch]);

  const allMembers = scientificResearch?.scientificResearchProfile || [];
  // Split into the same two boxes the Insert/Update modal uses.
  const teachers = allMembers.filter((m) => !m.isStudent);
  const students = allMembers.filter((m) => !!m.isStudent);
  const bonusCodes = scientificResearch?.bonusCodes || [];

  // Fetch the bonus config dictionary so we can render labels (not opaque codes) and
  // the active list of "Cách tính" reasons. Shared cached lookup; small payload.
  const [bonusConfigs, setBonusConfigs] = useState([]);
  useEffect(() => {
    if (!open) return;
    apiCall({ ...API_URLS.REWARD_BONUS_CONFIG.getAll() }).then(({ response, error }) => {
      if (!error && response?.status === 200 && response.data?.success) {
        setBonusConfigs(response.data.data || []);
      }
    });
  }, [open]);
  const bonusByCode = useMemo(() => {
    const m = {};
    bonusConfigs.forEach((c) => {
      m[c.code] = c;
    });
    return m;
  }, [bonusConfigs]);
  const formatBonusValue = (cfg) => {
    if (!cfg) return "";
    if (cfg.type === "override_value") return ` (${(cfg.value || 0).toLocaleString("vi-VN")} VND)`;
    if (cfg.type === "percent_add") return ` (+${cfg.value}%)`;
    if (cfg.type === "percent_sub") return ` (-${cfg.value}%)`;
    return "";
  };

  // Pull the explain payload so admins can see why an amount came out the way it did.
  // Reuses /researchRewards/explain — a dry-run of the formula. Re-fetched whenever
  // the dialog opens for a new paper.
  const [explain, setExplain] = useState(null);
  const [explainLoading, setExplainLoading] = useState(false);
  useEffect(() => {
    if (!open || !scientificResearch?.id) {
      setExplain(null);
      return;
    }
    setExplainLoading(true);
    const api = API_URLS.RESEARCH_REWARD.explain
      ? API_URLS.RESEARCH_REWARD.explain()
      : { endPoint: `/api/v1/researchRewards/explain`, method: "GET", headers: {} };
    apiCall({ ...api, endPoint: `${api.endPoint}?workId=${scientificResearch.id}` })
      .then(({ response, error }) => {
        if (!error && response?.status === 200 && response.data?.success) {
          setExplain(response.data.data);
        } else {
          setExplain(null);
        }
      })
      .finally(() => setExplainLoading(false));
  }, [open, scientificResearch?.id]);
  const formatVnd = (n) => (n || 0).toLocaleString("vi-VN") + " ₫";

  const propsValues = {
    values,
    updating: values,
    disabled: true,
    profileList: profileList || [],
    setValues: NOOP,
    handleInsert: NOOP,
    handleRemove: NOOP,
    handleChangeForm: NOOP,
    handleChangeProfileList: NOOP,
  };

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          width: "calc(100vw - 240px)",
          height: "100vh",
          maxWidth: "calc(100vw - 240px)",
          maxHeight: "100vh",
          margin: "0 0 0 240px",
        },
      }}
    >
      <DialogTitle
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 32px",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h5" style={{ fontWeight: 600 }}>
          {`Chi tiết bài báo ${getTypeTitle(filter)}`}
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          style={{ padding: "8px 24px", fontSize: "15px", fontWeight: 500, borderWidth: 2 }}
        >
          Đóng
        </Button>
      </DialogTitle>

      <DialogContent style={{ flex: 1, overflow: "hidden", display: "flex", padding: 0 }}>
        {isFetching || !values ? (
          <Box display="flex" justifyContent="center" alignItems="center" style={{ width: "100%", minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* ── Left panel: form fields (60%) ── */}
            <div
              style={{
                width: "60%",
                padding: "16px 24px",
                overflowY: "auto",
                borderRight: "1px solid #e0e0e0",
              }}
            >
              <Grid container spacing={2}>
                {/* Type-specific sub-form (all fields disabled) */}
                {filter === "journalInternational" && <JournalInternational {...propsValues} />}
                {filter === "journalDomestic" && <JournalDomestic {...propsValues} />}
                {filter === "conference" && <Conference {...propsValues} />}
                {filter === "conferenceDomestic" && <ConferenceDomestic {...propsValues} />}

                {/* Mục đích — chỉ render cho Chương sách vì không có sub-form tương ứng.
                    Các loại khác đã có dropdown Mục đích sẵn trong sub-form. */}
                {filter === "chapter" && (
                  <Grid item xs={12}>
                    <TextField
                      disabled
                      fullWidth
                      size="small"
                      label="Mục đích"
                      variant="outlined"
                      value={
                        purposes?.find?.((p) => p.id === values?.purposeId)?.description ||
                        (values?.purposeId === 2
                          ? "Tính thưởng"
                          : values?.purposeId === 1
                            ? "Tính KPI"
                            : values?.purposeId === 99999999
                              ? "Tính thưởng (cũ)"
                              : values?.purposeId
                                ? `#${values.purposeId}`
                                : "")
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                )}

                {/* Loại công trình (giờ NC) — ẩn cho chương sách giống form khai báo. */}
                {scientificResearch?.type !== "chapterJournal" && (
                  <Grid item xs={12}>
                    <TextField
                      disabled
                      fullWidth
                      size="small"
                      label="Loại công trình"
                      variant="outlined"
                      // Prefer the description so the user sees the human-readable name
                      // (BE preloads `TimeResearchDetail`; explain payload also has it).
                      value={
                        values?.timeResearchDetail?.description ||
                        explain?.timeResearchDetailDescription ||
                        values?.timeResearchDetailCode ||
                        ""
                      }
                      // helperText={
                      //   values?.timeResearchDetailCode ? `Mã: ${values.timeResearchDetailCode}` : ""
                      // }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                )}

                {/* Loại công trình tính thưởng — chỉ hiện khi mục đích = Tính thưởng. */}
                {(values?.purposeId === 2 || values?.purposeId === 99999999) && (
                  <Grid item xs={12}>
                    <TextField
                      disabled
                      fullWidth
                      size="small"
                      label="Loại công trình tính thưởng"
                      variant="outlined"
                      value={explain?.bonusDetailDescription || values?.timeResearchDetailCodeForBonus || ""}
                      // helperText={
                      //   values?.timeResearchDetailCodeForBonus
                      //     ? `Mã: ${values.timeResearchDetailCodeForBonus}`
                      //     : ""
                      // }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                )}
               {(values?.purposeId === 2 || values?.purposeId === 99999999) &&
                  (() => {
                    const effectiveCodes =
                      bonusCodes.length > 0
                        ? bonusCodes
                        : Array.isArray(explain?.bonusCodes)
                          ? explain.bonusCodes
                          : [];
                    return (
                      <>
                        <Grid item xs={5} style={{ margin: "auto" }}>
                          <Divider />
                        </Grid>
                        <Grid item xs={2}>
                          <Typography style={{ textAlign: "center", color: "rgba(0, 0, 0, 0.30)" }}>
                            Tiêu chí thưởng
                          </Typography>
                        </Grid>
                        <Grid item xs={5} style={{ margin: "auto" }}>
                          <Divider />
                        </Grid>
                        <Grid item xs={12}>
                          {effectiveCodes.length === 0 ? (
                            <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                              Không có tiêu chí thưởng nào được áp dụng.
                            </Typography>
                          ) : (
                            effectiveCodes.map((code) => {
                              const cfg = bonusByCode[code];
                              return (
                                <Box key={code} display="flex" alignItems="center" mb={0.5}>
                                  <Checkbox checked disabled size="small" color="primary" />
                                  <Typography variant="body2">
                                    {cfg?.label || code}
                                    {/* <span style={{ color: "#1976d2", fontWeight: 600 }}>
                                      {formatBonusValue(cfg)}
                                    </span> */}
                                    {cfg && !cfg.active && (
                                      <Chip size="small" label="đã tắt" style={{ marginLeft: 8 }} />
                                    )}
                                  </Typography>
                                </Box>
                              );
                            })
                          )}
                        </Grid>
                      </>
                    );
                  })()} 
                {/* Lĩnh vực bài báo */}
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel id="linhVucBaiBao-view-label" style={{ padding: "0 8px", backgroundColor: "white" }}>
                      Lĩnh vực bài báo
                    </InputLabel>
                    <Select
                      native
                      fullWidth
                      disabled
                      labelId="linhVucBaiBao-view-label"
                      value={values?.linhVucBaiBao || "KHAC"}
                    >
                      {LinhVucBaiBao.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Journal-specific fields */}
                {(filter === "journalInternational" || filter === "journalDomestic") && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        disabled
                        fullWidth
                        size="small"
                        label="Thuộc sản phẩm của đề tài"
                        variant="outlined"
                        value={values?.thuocDeTai || ""}
                        multiline
                        rows={3}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        disabled
                        fullWidth
                        size="small"
                        label="Tóm tắt"
                        variant="outlined"
                        value={values?.tomTat || ""}
                        multiline
                        rows={3}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </>
                )}

                {/* Conference-specific fields */}
                {(filter === "conference" || filter === "conferenceDomestic") && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        disabled
                        fullWidth
                        size="small"
                        label="Tóm tắt"
                        variant="outlined"
                        value={values?.tomTat || ""}
                        multiline
                        rows={3}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        disabled
                        control={<Checkbox disabled checked={values?.coTranhLuan || false} color="primary" />}
                        label="Bài báo có tranh luận"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        disabled
                        fullWidth
                        size="small"
                        label="Đơn vị tổ chức"
                        variant="outlined"
                        value={values?.donViToChuc || ""}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        disabled
                        fullWidth
                        size="small"
                        label="Địa điểm tổ chức"
                        variant="outlined"
                        value={values?.diaDiemToChuc || ""}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </>
                )}

                {/* Ghi chú */}
                <Grid item xs={12}>
                  <TextField
                    disabled
                    fullWidth
                    size="small"
                    label="Ghi chú"
                    variant="outlined"
                    value={values?.note || ""}
                    multiline
                    rows={3}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* ──────────────── Tiêu chí thưởng (read-only) ────────────────
                    Always rendered when purpose is Tính thưởng so admin sees "none
                    applied" explicitly rather than mistaking a hidden section for an
                    empty paper. Data source falls back to explain.bonusCodes if the
                    main record didn't include the array (legacy rows). */}
                

                {/* ──────────────── BOX 1: Thành viên ──────────────── */}
                <Grid item xs={5} style={{ margin: "auto" }}>
                  <Divider />
                </Grid>
                <Grid item xs={2}>
                  <Typography style={{ textAlign: "center", color: "rgba(0, 0, 0, 0.30)" }}>Thành viên</Typography>
                </Grid>
                <Grid item xs={5} style={{ margin: "auto" }}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  {teachers.length === 0 && (
                    <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                      Bài chưa có thành viên giảng viên/CB.
                    </Typography>
                  )}
                  {teachers.map((e, index) => {
                    const isInt = e.isInternal !== false;
                    const profileObj = profileList?.find((p) => p.id === e.profileId) || e.profile || {};
                    return (
                      <Grid
                        key={`view-member-${index}`}
                        container
                        spacing={2}
                        style={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 6,
                          padding: 12,
                          marginBottom: 12,
                          marginLeft: 0,
                          width: "100%",
                        }}
                      >
                        {/* Row 1 (xs sum = 12) */}
                        <Grid item xs={2}>
                          <TextField
                            disabled
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Loại"
                            value={isInt ? "Nội bộ" : "Ngoại bộ"}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        {isInt ? (
                          <>
                            <Grid item xs={4}>
                              <TextField
                                disabled
                                fullWidth
                                size="small"
                                variant="outlined"
                                label="Thành viên"
                                value={profileObj?.name || `ID: ${e.profileId}`}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                disabled
                                fullWidth
                                size="small"
                                variant="outlined"
                                label="Mã viên chức"
                                value={profileObj?.code || ""}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                disabled
                                fullWidth
                                size="small"
                                variant="outlined"
                                label="Đơn vị"
                                value={profileObj?.departmentProfile?.[0]?.department?.name || ""}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          </>
                        ) : (
                          <Grid item xs={10}>
                            <TextField
                              disabled
                              fullWidth
                              size="small"
                              variant="outlined"
                              label="Tên thành viên ngoại bộ"
                              value={e?.externalName || ""}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        )}
                        {/* Row 2 (xs sum = 12) */}
                        {isInt && (
                          <Grid item xs={2}>
                            <TextField
                              disabled
                              fullWidth
                              size="small"
                              variant="outlined"
                              label="Số ĐV"
                              value={e?.orgCount ?? 1}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        )}
                        <Grid item xs={isInt ? 5 : 7}>
                          <TextField
                            disabled
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Vai trò"
                            value={e?.roleInPaper === "main" ? "Tác giả chính" : "Đồng/không phải tác giả chính"}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <TextField
                            disabled
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Trạng thái"
                            value={
                              { NCS: "Đang làm NCS", CH: "Đang học Cao học", K: "Khác" }[e?.statusInProject] || "Khác"
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* ──────────────── BOX 2: Sinh viên ──────────────── */}
                <Grid item xs={5} style={{ margin: "auto" }}>
                  <Divider />
                </Grid>
                <Grid item xs={2}>
                  <Typography style={{ textAlign: "center", color: "rgba(0, 0, 0, 0.30)" }}>Sinh viên</Typography>
                </Grid>
                <Grid item xs={5} style={{ margin: "auto" }}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  {students.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                      Bài không có sinh viên / học viên.
                    </Typography>
                  ) : (
                    students.map((s, index) => (
                      <Grid
                        key={`view-student-${index}`}
                        container
                        spacing={2}
                        style={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 6,
                          padding: 12,
                          marginBottom: 12,
                          marginLeft: 0,
                          width: "100%",
                        }}
                      >
                        <Grid item xs={5}>
                          <TextField
                            disabled
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Tên sinh viên / học viên"
                            value={s?.studentName || ""}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField
                            disabled
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Mã sinh viên / học viên"
                            value={s?.studentCode || ""}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            disabled
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Mã lớp"
                            value={s?.studentClass || ""}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                    ))
                  )}
                </Grid>

                {/* ──────────────── Cách tính thưởng (debug) ──────────────── */}
                {/* Hidden by user request; restore by changing the guard below to `true`. */}
                {false && (<>
                <Grid item xs={5} style={{ margin: "auto" }}>
                  <Divider />
                </Grid>
                <Grid item xs={2}>
                  <Typography style={{ textAlign: "center", color: "rgba(0, 0, 0, 0.30)" }}>
                    Cách tính thưởng
                  </Typography>
                </Grid>
                <Grid item xs={5} style={{ margin: "auto" }}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  {explainLoading ? (
                    <Box display="flex" justifyContent="center" py={1}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : !explain ? (
                    <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                      Không tải được dữ liệu cách tính.
                    </Typography>
                  ) : (
                    <Box>
                      {Array.isArray(explain.notes) && explain.notes.length > 0 && (
                        <Box mb={1}>
                          {explain.notes.map((n, i) => (
                            <Typography
                              key={i}
                              variant="caption"
                              style={{ display: "block", color: "#d32f2f", fontStyle: "italic" }}
                            >
                              ⚠ {n}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      <Grid container spacing={1} style={{ marginBottom: 8 }}>
                        <Grid item xs={6}>
                          <Typography variant="caption" style={{ color: "#666" }}>
                            Loại công trình (giờ NC)
                          </Typography>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {explain.timeResearchDetailDescription ||
                              explain.timeResearchDetailCode ||
                              "—"}
                          </Typography>
                          {explain.timeResearchDetailDescription && (
                            <Typography variant="caption" style={{ color: "#888" }}>
                              Mã: <code>{explain.timeResearchDetailCode}</code>
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" style={{ color: "#666" }}>
                            Loại công trình tính thưởng
                          </Typography>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {explain.bonusDetailDescription || explain.bonusDetailCode || "—"}
                          </Typography>
                          {explain.bonusDetailCode && (
                            <Typography variant="caption" style={{ color: "#888" }}>
                              Mã: <code>{explain.bonusDetailCode}</code>
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" style={{ color: "#666" }}>
                            BonusCode (TimeResearchDetail)
                          </Typography>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {explain.bonusCode || "—"}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" style={{ color: "#666" }}>
                            rewardValue 
                          </Typography>
                          <Typography variant="body2" style={{ fontWeight: 600, color: "#1976d2" }}>
                            {formatVnd(explain.rewardValue)}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" style={{ color: "#666" }}>
                            Số tác giả (numOfMember)
                          </Typography>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {explain.numOfMember}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" style={{ color: "#666" }}>
                            baseValue (sau override)
                          </Typography>
                          <Typography variant="body2" style={{ fontWeight: 600, color: "#1976d2" }}>
                            {formatVnd(explain.baseValue)}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" style={{ color: "#666" }}>
                            bonusPercent
                          </Typography>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {(explain.bonusPercent * 100).toFixed(1)}%
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" style={{ color: "#666" }}>
                            Locked
                          </Typography>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {explain.locked || "—"}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" style={{ color: "#666" }}>
                            PurposeId
                          </Typography>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {explain.purposeId}
                          </Typography>
                        </Grid>
                        {explain.hasStudent && (
                          <>
                            <Grid item xs={3}>
                              <Typography variant="caption" style={{ color: "#666" }}>
                                Số sinh viên
                              </Typography>
                              <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {explain.numStudents}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="caption" style={{ color: "#666" }}>
                                % giảm cho SV
                              </Typography>
                              <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {(explain.studentShare * 100).toFixed(0)}%
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" style={{ color: "#666" }}>
                                Tổng tiền cho sinh viên
                              </Typography>
                              <Typography variant="body2" style={{ fontWeight: 600, color: "#1976d2" }}>
                                {formatVnd(explain.studentTotal)}
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>

                      <Typography variant="subtitle2" style={{ fontWeight: 600, marginTop: 8 }}>
                        Phân bổ theo từng thành viên
                      </Typography>
                      <Typography variant="caption" style={{ display: "block", color: "#666", marginBottom: 4 }}>
                        GV nội bộ — Tác giả chính: x/2 + x/(2n) · Đồng tác giả: x/(2n).{" "}
                        Sinh viên (không xét vai trò): x/(2n)
                        {explain.hasStudent ? ` × (1 − ${(explain.studentShare * 100).toFixed(0)}%)` : ""}.
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Thành viên</TableCell>
                              <TableCell>Vai trò</TableCell>
                              <TableCell>Loại</TableCell>
                              <TableCell align="right">orgRatio</TableCell>
                              <TableCell align="right">Tiền (dự kiến)</TableCell>
                              <TableCell>Ghi chú</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(explain.members || []).map((m, i) => (
                              <TableRow key={i}>
                                <TableCell>{m.name || `#${m.profileId}` || m.studentName || "—"}</TableCell>
                                <TableCell>
                                  {/* Sinh viên không xét vai trò; hiển thị "—" để không gây hiểu lầm. */}
                                  {m.isStudent ? "—" : m.isMain ? "Tác giả chính" : "Đồng tác giả"}
                                </TableCell>
                                <TableCell>
                                  {m.isStudent ? "Sinh viên" : m.isInternal ? "Nội bộ" : "Ngoại bộ"}
                                </TableCell>
                                <TableCell align="right">
                                  {m.isStudent ? "—" : m.orgRatio?.toFixed?.(4) ?? m.orgRatio}
                                </TableCell>
                                <TableCell align="right" style={{ color: m.computedAmount > 0 ? "#1976d2" : "#aaa" }}>
                                  {m.computedAmount ? formatVnd(m.computedAmount) : "—"}
                                </TableCell>
                                <TableCell style={{ fontStyle: "italic", color: "#888" }}>
                                  {m.skippedReason || ""}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Grid>
                </>)}
              </Grid>
            </div>

            {/* ── Right panel: file viewer (40%) ── */}
            <div
              style={{
                width: "40%",
                padding: "16px 24px",
                overflowY: "auto",
                backgroundColor: "#fafafa",
              }}
            >
              <Typography variant="h6" style={{ marginBottom: 16 }}>
                File minh chứng
              </Typography>
              <MultiFileManager
                fileUrls={fileUrls}
                setFileUrls={NOOP}
                newFiles={[]}
                setNewFiles={NOOP}
                disabled={true}
              />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const mapStateToProps = (state) => ({
  profileList: select(state, "profileReducer", "list"),
  // Purpose list — used to render the "Mục đích" dropdown in the sub-form and the
  // standalone textfield in chapter mode. Loaded on demand when missing (see useEffect).
  purposes: select(state, "scientificResearchReducer", "purposes"),
});

const mapDispatchToProps = (dispatch) => ({
  getAllPurpose: () => dispatch(getAllPurpose()),
});

export default connect(mapStateToProps, mapDispatchToProps)(toJs(ScientificResearchViewModal));

